<template>
  <!-- Desktop の Dialog は grid gap-4 で本文上に 16px の余白が入るため、md:-mt-4 で相殺して地図を上端に密着させる。
       Dialog / Drawer とも高さ制約を持たないため、venueDetail が長いと内容が画面外へはみ出し、
       下部が読めず・押せなくなる。ここで高さ上限＋スクロールを持たせる
       （座標なし時に使う LocationDetail.vue と同じ方針）。overscroll-contain で背面へのスクロール連鎖を防ぐ。 -->
  <div class="max-h-[80dvh] w-full max-w-[640px] overflow-y-auto overflow-x-hidden overscroll-contain rounded-lg md:-mt-4">
    <!-- Static マップ（backend が発行する署名付き URL）。署名を壊さないため OptimizedImage は通さず
         生 <img> で表示する。intrinsic 640x360（scale=2 の CSS サイズ）で CLS を防ぐ。
         モーダル幅いっぱいのエッジ表示にして余白を作らない（周囲の padding は付けない）。
         読み込み失敗時はプレースホルダにフォールバック（Open in Google Maps はマップ直下のリンクへ集約）。 -->
    <img
      v-if="staticMapUrl && !isImageError"
      alt="Map of the experience location and nearest stations"
      class="block h-auto w-full"
      height="360"
      loading="lazy"
      width="640"
      :src="staticMapUrl"
      @error="isImageError = true"
    />
    <!-- 取得中は「利用不可」ではなくローディングを表示する（取得完了前に誤って失敗表示しない）。 -->
    <div v-else-if="isMapLoading" class="flex min-h-[200px] items-center justify-center bg-beige1 text-[13px] text-gray-500">Loading...</div>
    <div v-else class="flex min-h-[200px] flex-col items-center justify-center gap-2 bg-beige1 p-6 text-center">
      <img alt="" height="24" width="24" :src="locationIconUrl" />
      <p class="text-[13px] text-gray-500">Map is unavailable.</p>
    </div>

    <!-- コンテンツ。地図はエッジまで、テキスト部だけ適切な padding を付ける。 -->
    <div class="px-5 pb-7 pt-6 md:px-8">
      <!-- 座標由来の Google Maps 導線。Static マップの取得成否・ローディングとは無関係なので、
           座標（googleMapsUrl）があればマップ状態に関わらず常に表示する。
           venueDetail が長いとページ最下部ではスクロールしないと押せないため、マップ直下に置く。 -->
      <a
        v-if="googleMapsUrl"
        aria-label="Open in Google Maps (opens in a new tab)"
        class="mb-6 inline-flex items-center text-[14px] text-gold1 underline underline-offset-2 md:text-[15px]"
        rel="noopener noreferrer"
        target="_blank"
        :href="googleMapsUrl"
        >Open in Google Maps ↗</a
      >

      <!-- 最寄り駅（英語表記のみ）。取得中はスケルトン、0件・エラー時はセクションを出さない -->
      <section v-if="isLoading || stations.length > 0">
        <h2 class="text-[16px] font-medium md:text-[17px]">Nearest stations</h2>
        <div v-if="isLoading" class="mt-3 text-[13px] text-gray-500">Loading...</div>
        <ul v-else class="mt-2 flex flex-col divide-y divide-beige3/60">
          <!-- 番号バッジは Static マップの駅ピンのラベル（backend が rank 順に 1..N を付与）と対応する。
               ピンと同じ色（stationMarkerColor: #555555）にして「どのピンがどの駅か」を判別できるようにする。 -->
          <li v-for="(station, index) in stationLabels" :key="station.rank" class="flex items-start justify-between gap-x-4 py-2.5 text-[14px] md:text-[15px]">
            <span class="flex min-w-0 items-start gap-x-2">
              <span class="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#555555] text-[11px] leading-none text-white">{{ index + 1 }}</span>
              <span class="break-words">{{ station.name }}</span>
            </span>
            <span class="shrink-0 text-gold1">{{ station.label }}</span>
          </li>
        </ul>
      </section>

      <!-- 元々「Location Details」として表示していた開催地の詳細（venueDetail）を最寄り駅の下に配置する。 -->
      <section v-if="venueDetail" :class="isLoading || stations.length > 0 ? 'mt-7 border-t border-beige3/60 pt-6' : ''">
        <h2 class="text-[16px] font-medium md:text-[17px]">Location details</h2>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <p class="mt-3 break-words text-[14px] leading-relaxed md:text-[15px]" v-html="venueDetailHtml"></p>
      </section>
    </div>
  </div>
</template>

<script lang="ts" setup>
import locationIconUrl from '@/assets/icon/location/default.svg';
import { convertNewlinesToBreaks } from '@/utils/stringUtils';
import type { NearestStation } from '@/wabunka-src/features/Experience/composables/query/useNearestStationsQuery';
import { formatNearestStationLabel } from '@/wabunka-src/features/Experience/utils/nearestStationDisplay';

interface Props {
  staticMapUrl: string;
  stations: NearestStation[];
  origin: { latitude: number; longitude: number };
  googleMapsUrl: string | null;
  isLoading: boolean;
  isMapLoading: boolean;
  // 元々「Location Details」で表示していた開催地の詳細テキスト（無ければ非表示）。
  venueDetail?: string;
}

const props = defineProps<Props>();

const isImageError = ref(false);

// template 内で関数を呼ぶと再レンダリング毎に評価されるため computed に切り出す（駅ラベル・改行変換）。
const stationLabels = computed(() => props.stations.map((station) => ({ ...station, label: formatNearestStationLabel(station, props.origin) })));
const venueDetailHtml = computed(() => (props.venueDetail ? convertNewlinesToBreaks(props.venueDetail) : ''));
</script>
